/**
 * E2E Flow Template Presets
 *
 * Part of Issue #258 - End-to-End User Interaction Flow Diagrams
 * Implementation: Issue #311
 *
 * Pre-configured templates for different visualization styles:
 * - Default: Clean, balanced color scheme
 * - Swimlane: Organized by layer (frontend/backend/database)
 * - Debug: Detailed view with source links
 * - Minimal: Simplified view for presentations
 * - Dark: Dark theme variant
 */
// ============= Step Style Presets =============
/**
 * Default step styles (light theme)
 */
export const defaultStepStyles = {
    'user-action': {
        backgroundColor: '#E3F2FD',
        borderColor: '#1976D2',
        icon: 'cursor-click',
        iconColor: '#1976D2',
        shape: 'rounded',
    },
    'ui-component': {
        backgroundColor: '#E8F5E9',
        borderColor: '#388E3C',
        icon: 'component',
        iconColor: '#388E3C',
        shape: 'rounded',
    },
    'api-call': {
        backgroundColor: '#FFF3E0',
        borderColor: '#F57C00',
        icon: 'arrow-right-circle',
        iconColor: '#F57C00',
        shape: 'rectangle',
    },
    'api-route': {
        backgroundColor: '#FCE4EC',
        borderColor: '#C2185B',
        icon: 'server',
        iconColor: '#C2185B',
        shape: 'rectangle',
    },
    'service-call': {
        backgroundColor: '#F3E5F5',
        borderColor: '#7B1FA2',
        icon: 'cube',
        iconColor: '#7B1FA2',
        shape: 'rectangle',
    },
    'database': {
        backgroundColor: '#E0F2F1',
        borderColor: '#00796B',
        icon: 'database',
        iconColor: '#00796B',
        shape: 'hexagon',
    },
    'external-api': {
        backgroundColor: '#FBE9E7',
        borderColor: '#E64A19',
        icon: 'cloud',
        iconColor: '#E64A19',
        shape: 'diamond',
    },
    'state-change': {
        backgroundColor: '#E8EAF6',
        borderColor: '#3F51B5',
        icon: 'refresh',
        iconColor: '#3F51B5',
        shape: 'rounded',
    },
    'navigation': {
        backgroundColor: '#E1F5FE',
        borderColor: '#0288D1',
        icon: 'navigation',
        iconColor: '#0288D1',
        shape: 'rounded',
    },
    'error': {
        backgroundColor: '#FFEBEE',
        borderColor: '#D32F2F',
        icon: 'alert-circle',
        iconColor: '#D32F2F',
        shape: 'diamond',
    },
    'cache': {
        backgroundColor: '#FFFDE7',
        borderColor: '#FBC02D',
        icon: 'database-cog',
        iconColor: '#FBC02D',
        shape: 'hexagon',
    },
};
/**
 * Dark theme step styles
 */
export const darkStepStyles = {
    'user-action': {
        backgroundColor: '#1E3A5F',
        borderColor: '#64B5F6',
        icon: 'cursor-click',
        iconColor: '#64B5F6',
        textColor: '#E3F2FD',
        shape: 'rounded',
    },
    'ui-component': {
        backgroundColor: '#1B4332',
        borderColor: '#81C784',
        icon: 'component',
        iconColor: '#81C784',
        textColor: '#E8F5E9',
        shape: 'rounded',
    },
    'api-call': {
        backgroundColor: '#4E342E',
        borderColor: '#FFB74D',
        icon: 'arrow-right-circle',
        iconColor: '#FFB74D',
        textColor: '#FFF3E0',
        shape: 'rectangle',
    },
    'api-route': {
        backgroundColor: '#4A1942',
        borderColor: '#F48FB1',
        icon: 'server',
        iconColor: '#F48FB1',
        textColor: '#FCE4EC',
        shape: 'rectangle',
    },
    'service-call': {
        backgroundColor: '#311B47',
        borderColor: '#CE93D8',
        icon: 'cube',
        iconColor: '#CE93D8',
        textColor: '#F3E5F5',
        shape: 'rectangle',
    },
    'database': {
        backgroundColor: '#004D40',
        borderColor: '#4DB6AC',
        icon: 'database',
        iconColor: '#4DB6AC',
        textColor: '#E0F2F1',
        shape: 'hexagon',
    },
    'external-api': {
        backgroundColor: '#3E2723',
        borderColor: '#FF8A65',
        icon: 'cloud',
        iconColor: '#FF8A65',
        textColor: '#FBE9E7',
        shape: 'diamond',
    },
    'state-change': {
        backgroundColor: '#283593',
        borderColor: '#9FA8DA',
        icon: 'refresh',
        iconColor: '#9FA8DA',
        textColor: '#E8EAF6',
        shape: 'rounded',
    },
    'navigation': {
        backgroundColor: '#01579B',
        borderColor: '#4FC3F7',
        icon: 'navigation',
        iconColor: '#4FC3F7',
        textColor: '#E1F5FE',
        shape: 'rounded',
    },
    'error': {
        backgroundColor: '#4A1414',
        borderColor: '#EF5350',
        icon: 'alert-circle',
        iconColor: '#EF5350',
        textColor: '#FFEBEE',
        shape: 'diamond',
    },
    'cache': {
        backgroundColor: '#4A4000',
        borderColor: '#FFEE58',
        icon: 'database-cog',
        iconColor: '#FFEE58',
        textColor: '#FFFDE7',
        shape: 'hexagon',
    },
};
/**
 * Minimal step styles (grayscale)
 */
export const minimalStepStyles = {
    'user-action': {
        backgroundColor: '#F5F5F5',
        borderColor: '#424242',
        icon: 'cursor-click',
        shape: 'rounded',
    },
    'ui-component': {
        backgroundColor: '#FAFAFA',
        borderColor: '#616161',
        icon: 'component',
        shape: 'rounded',
    },
    'api-call': {
        backgroundColor: '#EEEEEE',
        borderColor: '#757575',
        icon: 'arrow-right-circle',
        shape: 'rectangle',
    },
    'api-route': {
        backgroundColor: '#E0E0E0',
        borderColor: '#9E9E9E',
        icon: 'server',
        shape: 'rectangle',
    },
    'service-call': {
        backgroundColor: '#BDBDBD',
        borderColor: '#757575',
        icon: 'cube',
        shape: 'rectangle',
    },
    'database': {
        backgroundColor: '#9E9E9E',
        borderColor: '#424242',
        icon: 'database',
        textColor: '#FFFFFF',
        shape: 'hexagon',
    },
    'external-api': {
        backgroundColor: '#757575',
        borderColor: '#212121',
        icon: 'cloud',
        textColor: '#FFFFFF',
        shape: 'diamond',
    },
    'state-change': {
        backgroundColor: '#F5F5F5',
        borderColor: '#424242',
        icon: 'refresh',
        shape: 'rounded',
    },
    'navigation': {
        backgroundColor: '#EEEEEE',
        borderColor: '#616161',
        icon: 'navigation',
        shape: 'rounded',
    },
    'error': {
        backgroundColor: '#FFCDD2',
        borderColor: '#B71C1C',
        icon: 'alert-circle',
        shape: 'diamond',
    },
    'cache': {
        backgroundColor: '#E0E0E0',
        borderColor: '#757575',
        icon: 'database-cog',
        shape: 'hexagon',
    },
};
// ============= Template Presets =============
/**
 * Default template - Clean, balanced view
 */
export const defaultTemplate = {
    name: 'Default',
    description: 'Clean, balanced color scheme for everyday use',
    stepStyles: defaultStepStyles,
    edgeStyles: {
        default: {
            strokeColor: '#9E9E9E',
            strokeWidth: 2,
        },
        http: {
            strokeColor: '#F57C00',
            strokeWidth: 2,
            animated: true,
        },
        async: {
            strokeColor: '#7B1FA2',
            strokeWidth: 2,
            strokeDasharray: '5,5',
        },
        database: {
            strokeColor: '#00796B',
            strokeWidth: 2,
        },
        file: {
            strokeColor: '#795548',
            strokeWidth: 2,
        },
    },
    layout: {
        direction: 'horizontal',
        nodeSpacing: 40,
        levelSpacing: 100,
        groupByType: false,
    },
    interactions: {
        clickToSource: true,
        hoverDetails: true,
        highlightFlow: true,
        filterByType: true,
    },
};
/**
 * Swimlane template - Organized by layer
 */
export const swimlaneTemplate = {
    name: 'Swimlane',
    description: 'Organized by layer (frontend/backend/database)',
    stepStyles: defaultStepStyles,
    edgeStyles: {
        default: {
            strokeColor: '#9E9E9E',
            strokeWidth: 2,
        },
        http: {
            strokeColor: '#F57C00',
            strokeWidth: 3,
            animated: true,
        },
        async: {
            strokeColor: '#7B1FA2',
            strokeWidth: 2,
            strokeDasharray: '8,4',
        },
        database: {
            strokeColor: '#00796B',
            strokeWidth: 2,
        },
        file: {
            strokeColor: '#795548',
            strokeWidth: 2,
        },
    },
    layout: {
        direction: 'vertical',
        nodeSpacing: 60,
        levelSpacing: 150,
        groupByType: true,
    },
    interactions: {
        clickToSource: true,
        hoverDetails: true,
        highlightFlow: true,
        filterByType: true,
    },
};
/**
 * Debug template - Detailed view with source links
 */
export const debugTemplate = {
    name: 'Debug',
    description: 'Detailed view with source code links and execution info',
    stepStyles: {
        ...defaultStepStyles,
        error: {
            ...defaultStepStyles.error,
            backgroundColor: '#B71C1C',
            textColor: '#FFFFFF',
        },
    },
    edgeStyles: {
        default: {
            strokeColor: '#616161',
            strokeWidth: 2,
        },
        http: {
            strokeColor: '#F57C00',
            strokeWidth: 3,
            animated: true,
        },
        async: {
            strokeColor: '#7B1FA2',
            strokeWidth: 2,
            strokeDasharray: '5,5',
            animated: true,
        },
        database: {
            strokeColor: '#00796B',
            strokeWidth: 2,
        },
        file: {
            strokeColor: '#795548',
            strokeWidth: 2,
        },
    },
    layout: {
        direction: 'horizontal',
        nodeSpacing: 60,
        levelSpacing: 120,
        groupByType: false,
    },
    interactions: {
        clickToSource: true,
        hoverDetails: true,
        highlightFlow: true,
        filterByType: true,
    },
};
/**
 * Minimal template - Simplified for presentations
 */
export const minimalTemplate = {
    name: 'Minimal',
    description: 'Simplified grayscale view for presentations',
    stepStyles: minimalStepStyles,
    edgeStyles: {
        default: {
            strokeColor: '#757575',
            strokeWidth: 1,
        },
        http: {
            strokeColor: '#424242',
            strokeWidth: 2,
        },
        async: {
            strokeColor: '#616161',
            strokeWidth: 1,
            strokeDasharray: '4,4',
        },
        database: {
            strokeColor: '#424242',
            strokeWidth: 1,
        },
        file: {
            strokeColor: '#757575',
            strokeWidth: 1,
        },
    },
    layout: {
        direction: 'horizontal',
        nodeSpacing: 50,
        levelSpacing: 120,
        groupByType: false,
    },
    interactions: {
        clickToSource: false,
        hoverDetails: true,
        highlightFlow: true,
        filterByType: false,
    },
};
/**
 * Dark template - Dark theme variant
 */
export const darkTemplate = {
    name: 'Dark',
    description: 'Dark theme for low-light environments',
    stepStyles: darkStepStyles,
    edgeStyles: {
        default: {
            strokeColor: '#616161',
            strokeWidth: 2,
        },
        http: {
            strokeColor: '#FFB74D',
            strokeWidth: 2,
            animated: true,
        },
        async: {
            strokeColor: '#CE93D8',
            strokeWidth: 2,
            strokeDasharray: '5,5',
        },
        database: {
            strokeColor: '#4DB6AC',
            strokeWidth: 2,
        },
        file: {
            strokeColor: '#A1887F',
            strokeWidth: 2,
        },
    },
    layout: {
        direction: 'horizontal',
        nodeSpacing: 40,
        levelSpacing: 100,
        groupByType: false,
    },
    interactions: {
        clickToSource: true,
        hoverDetails: true,
        highlightFlow: true,
        filterByType: true,
    },
};
// ============= Preset Collection =============
/**
 * All available template presets
 */
export const templatePresets = {
    default: defaultTemplate,
    swimlane: swimlaneTemplate,
    debug: debugTemplate,
    minimal: minimalTemplate,
    dark: darkTemplate,
};
/**
 * Get template preset by name
 */
export function getTemplatePreset(name) {
    return templatePresets[name] || defaultTemplate;
}
/**
 * Get all preset names
 */
export function getTemplatePresetNames() {
    return Object.keys(templatePresets);
}
/**
 * Get style for a step type from template
 */
export function getStepStyle(template, stepType) {
    return template.stepStyles[stepType] || defaultStepStyles['ui-component'];
}
//# sourceMappingURL=e2eFlowPresets.js.map